import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Copy,
  Check,
  Wand2,
  Trash2,
  Moon,
  Sun,
  ArrowRight,
  Smile,
  Inbox,
  PenLine,
  Flame,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { VistalaroLogo } from "@/components/VistalaroLogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_LENGTH = 3000;
const LOGO =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_respectful-reply-9/artifacts/v9a7gbgs_Logo%20Mark%203a%20Export-selection.png";

const POSITIVE_TONES = [
  { id: "diplomatique", label: "Diplomatique" },
  { id: "ferme", label: "Ferme & courtois" },
  { id: "neutre", label: "Neutre & factuel" },
];

const NEGATIVE_TONES = [
  { id: "fuck", label: "Fuck this shit" },
  { id: "trop_con", label: "T'es trop con" },
  { id: "dikkenek", label: "Dikketuche" },
];

const NEGATIVE_IDS = NEGATIVE_TONES.map((t) => t.id);

export default function Home() {
  const [incomingEmail, setIncomingEmail] = useState("");
  const [rawText, setRawText] = useState("");
  const [tone, setTone] = useState("diplomatique");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [dark, setDark] = useState(false);

  const isChaos = NEGATIVE_IDS.includes(tone);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  const handleCivilize = async () => {
    if (!rawText.trim()) {
      toast.error("Veuillez saisir votre brouillon de réponse.");
      return;
    }
    setLoading(true);
    setResult("");
    try {
      const { data } = await axios.post(`${API}/civilize`, {
        text: rawText,
        tone,
        incoming_email: incomingEmail,
      });
      setResult(data.civilized_text);
      toast.success(
        isChaos ? "Défoulement généré. Mille sabords !" : "Votre réponse a été reformulée."
      );
    } catch (e) {
      const msg = e?.response?.data?.detail || "Une erreur est survenue. Réessayez.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!result) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(result);
      } else {
        const ta = document.createElement("textarea");
        ta.value = result;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("Message copié dans le presse-papier !");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Impossible de copier automatiquement. Sélectionnez le texte manuellement.");
    }
  };

  const handleClear = () => {
    setIncomingEmail("");
    setRawText("");
    setResult("");
  };

  return (
    <div className="min-h-screen sky-bg text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-primary border-b-[3px] border-foreground">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-[76px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={LOGO}
              alt="Don't Be An Asshole"
              className="h-12 w-12 object-contain drop-shadow-[2px_2px_0_rgba(23,32,42,0.25)]"
            />
            <div className="leading-none">
              <div className="font-heading text-lg sm:text-2xl text-foreground tracking-wide">
                Don't Be An Asshole
              </div>
              <div className="eyebrow text-[10px] text-foreground/70 mt-0.5">
                Le civilisateur d'e-mails
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <a
              href="https://vistalaro.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-2"
              data-testid="powered-by-vistalaro"
            >
              <span className="eyebrow text-[10px] text-foreground/70">
                Propulsé par
              </span>
              <VistalaroLogo className="h-4 w-auto text-foreground" />
            </a>
            <Button
              data-testid="theme-toggle-button"
              variant="ghost"
              size="icon"
              onClick={() => setDark((d) => !d)}
              className="rounded-full border-[3px] border-foreground bg-card toon-shadow-sm toon-press hover:bg-card"
            >
              {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        {/* Hero */}
        <div className="max-w-3xl mb-10 md:mb-14 animate-fade-up">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-foreground leading-[1.05]">
            Répondons ensemble à ce{" "}
            <span className="text-toon-red [-webkit-text-stroke:1px_#17202A]">
              trou du cul
            </span>
            .
          </h1>
          <p className="mt-5 text-base sm:text-lg text-foreground/80 leading-relaxed font-medium bg-card/70 dark:bg-card/60 inline-block px-4 py-2 rounded-xl border-[3px] border-foreground toon-shadow-sm">
            Collez votre brouillon, même sous le coup de la colère. On vous rend
            une version pro, polie et constructive — ou bien un bon gros
            défouloir, c'est vous qui choisissez. D'oh !
          </p>
        </div>

        {/* Workspace */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">
          {/* Left: inputs */}
          <div className="flex flex-col gap-6">
            {/* Step 1 — Incoming email (optional) */}
            <Card className="p-5 sm:p-6 rounded-2xl border-[3px] border-foreground toon-shadow bg-card border-dashed">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="h-9 w-9 rounded-xl bg-toon-blue border-[3px] border-foreground flex items-center justify-center">
                    <Inbox className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h2 className="font-heading text-base leading-none">
                      E-mail reçu
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1 font-medium">
                      Le message auquel vous répondez
                    </p>
                  </div>
                </div>
                <span className="text-[10px] eyebrow px-2.5 py-1 rounded-full border-[2px] border-foreground bg-background text-foreground">
                  Optionnel
                </span>
              </div>
              <Textarea
                data-testid="incoming-email-input"
                value={incomingEmail}
                onChange={(e) =>
                  setIncomingEmail(e.target.value.slice(0, MAX_LENGTH))
                }
                placeholder="Collez ici l'e-mail de la personne à qui vous répondez pour obtenir une réponse mieux adaptée (facultatif)..."
                className="min-h-[110px] resize-none text-sm leading-relaxed bg-background rounded-xl border-[3px] border-foreground focus-visible:ring-toon-yellow"
              />
            </Card>

            {/* Step 2 — Your draft (required) */}
            <Card className="p-5 sm:p-6 rounded-2xl border-[3px] border-foreground toon-shadow bg-card">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="h-9 w-9 rounded-xl bg-primary border-[3px] border-foreground flex items-center justify-center">
                    <PenLine className="h-4 w-4 text-foreground" />
                  </div>
                  <div>
                    <h2 className="font-heading text-base leading-none">
                      Votre brouillon
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1 font-medium">
                      Votre réponse à reformuler
                    </p>
                  </div>
                </div>
                {(rawText || incomingEmail) && (
                  <Button
                    data-testid="clear-button"
                    variant="ghost"
                    size="sm"
                    onClick={handleClear}
                    className="text-foreground h-8 font-semibold hover:bg-secondary"
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" /> Effacer
                  </Button>
                )}
              </div>

              <Textarea
                data-testid="raw-email-input"
                value={rawText}
                onChange={(e) => setRawText(e.target.value.slice(0, MAX_LENGTH))}
                placeholder="Écrivez ou collez ici votre réponse, même énervée. On la rendra présentable..."
                className="min-h-[200px] resize-none text-sm sm:text-base leading-relaxed bg-background rounded-xl border-[3px] border-foreground focus-visible:ring-toon-yellow"
              />
              <div className="mt-2 text-right text-xs text-muted-foreground font-semibold">
                {rawText.length} / {MAX_LENGTH}
              </div>

              {/* Tone selector */}
              <div className="mt-5 space-y-3">
                <div>
                  <p className="eyebrow text-[11px] text-foreground mb-2">
                    On reste poli
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {POSITIVE_TONES.map((t) => (
                      <button
                        key={t.id}
                        data-testid={`tone-option-${t.id}`}
                        onClick={() => setTone(t.id)}
                        className={`rounded-xl border-[3px] border-foreground px-2 py-2 text-center text-xs font-bold leading-tight toon-press transition-all ${
                          tone === t.id
                            ? "bg-primary toon-shadow-sm"
                            : "bg-background hover:bg-secondary"
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="eyebrow text-[11px] text-toon-red mb-2 flex items-center gap-1">
                    <Flame className="h-3.5 w-3.5" /> On se lâche
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {NEGATIVE_TONES.map((t) => (
                      <button
                        key={t.id}
                        data-testid={`tone-option-${t.id}`}
                        onClick={() => setTone(t.id)}
                        className={`rounded-xl border-[3px] border-foreground px-2 py-2 text-center text-xs font-bold leading-tight toon-press transition-all ${
                          tone === t.id
                            ? "bg-toon-red text-white toon-shadow-sm"
                            : "bg-background text-toon-red hover:bg-secondary"
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <Button
                data-testid="civilize-submit-button"
                onClick={handleCivilize}
                disabled={loading}
                className={`mt-6 w-full h-14 text-lg font-heading tracking-wide rounded-xl border-[3px] border-foreground toon-shadow toon-press ${
                  isChaos
                    ? "bg-toon-red hover:bg-toon-red text-white"
                    : "bg-primary hover:bg-primary text-foreground"
                }`}
              >
                {loading ? (
                  <>
                    <Wand2 className="h-5 w-5 mr-2 animate-pulse" />
                    {isChaos ? "Mise à feu..." : "Transformation..."}
                  </>
                ) : isChaos ? (
                  <>
                    <Flame className="h-5 w-5 mr-2" />
                    Déchaîne-toi !
                  </>
                ) : (
                  <>
                    <Wand2 className="h-5 w-5 mr-2" />
                    Civiliser ma réponse
                  </>
                )}
              </Button>
            </Card>
          </div>

          {/* Right: output */}
          <Card
            data-testid="civilized-email-output"
            className="p-5 sm:p-6 flex flex-col rounded-2xl border-[3px] border-foreground toon-shadow bg-card lg:sticky lg:top-28 min-h-[420px]"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div
                  className={`h-9 w-9 rounded-xl border-[3px] border-foreground flex items-center justify-center ${
                    isChaos ? "bg-toon-red" : "bg-primary"
                  }`}
                >
                  {isChaos ? (
                    <Flame className="h-4 w-4 text-white" />
                  ) : (
                    <Smile className="h-4 w-4 text-foreground" />
                  )}
                </div>
                <div>
                  <h2 className="font-heading text-base leading-none">
                    {isChaos ? "Version explosive" : "Version civilisée"}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-1 font-medium">
                    {isChaos ? "À vos risques et périls" : "Prête à copier et envoyer"}
                  </p>
                </div>
              </div>
              {result && (
                <Button
                  data-testid="copy-button"
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  className="h-9 font-bold rounded-xl border-[3px] border-foreground bg-background hover:bg-secondary toon-shadow-sm toon-press"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 mr-1.5 text-toon-blue" /> Copié
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-1.5" /> Copier
                    </>
                  )}
                </Button>
              )}
            </div>

            <div className="flex-1">
              <AnimatePresence mode="wait">
                {loading ? (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-3 pt-2"
                  >
                    {[...Array(5)].map((_, i) => (
                      <div
                        key={i}
                        className="h-4 rounded-full bg-primary/40 animate-pulse"
                        style={{ width: `${90 - i * 8}%` }}
                      />
                    ))}
                  </motion.div>
                ) : result ? (
                  <motion.div
                    key="result"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="whitespace-pre-wrap text-sm sm:text-base leading-relaxed text-foreground font-medium"
                    data-testid="civilized-text-content"
                  >
                    {result}
                  </motion.div>
                ) : (
                  <motion.div
                    key="empty"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="h-full flex flex-col items-center justify-center text-center text-muted-foreground py-14"
                  >
                    <div className="h-16 w-16 rounded-2xl bg-secondary border-[3px] border-foreground flex items-center justify-center mb-4">
                      <ArrowRight className="h-6 w-6 text-foreground" />
                    </div>
                    <p className="text-sm max-w-xs font-medium">
                      Votre réponse reformulée apparaîtra ici, prête à copier.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </Card>
        </div>
      </main>

      <footer className="border-t-[3px] border-foreground mt-12 bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground font-medium">
          <span>Don't Be An Asshole — Communiquer avec assurance, calme et respect.</span>
          <a
            href="https://vistalaro.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2"
          >
            <span className="eyebrow text-[10px]">Propulsé par</span>
            <VistalaroLogo className="h-4 w-auto text-foreground" />
          </a>
        </div>
      </footer>
    </div>
  );
}
